<script setup>
  import { ref, onMounted, defineProps } from 'vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  import {
    listSubscriptions,
    setSubscriptionEnabled,
    testSubscription,
    deleteSubscription,
  } from '@/api/registrationSubscription'
  import SubscriptionEditor from './SubscriptionEditor.vue'
  import SubscriptionId from './SubscriptionId.vue'
  import DeliveryRecords from './DeliveryRecords.vue'
  import ConnectivityTest from './ConnectivityTest.vue'
  import HistoryPush from './HistoryPush.vue'
  import SubscriptionGuide from './SubscriptionGuide.vue'

  const props = defineProps({ meetingCode: { type: String, required: true } })
  const rows = ref([])
  const loading = ref(false)
  const failed = ref(false)
  const busy = ref(false)
  const editing = ref(null)
  const editorOpen = ref(false)
  const recordsFor = ref(null)
  const connectivityFor = ref(null)
  const historyFor = ref(null)
  const guideOpen = ref(false)
  const guideFor = ref('')
  function openGuide(row) {
    guideFor.value = row?.id ?? ''
    guideOpen.value = true
  }
  function createFromGuide() {
    guideOpen.value = false
    edit(null)
  }
  async function remove(row) {
    if (busy.value) return
    try {
      await ElMessageBox.confirm(
        `删除「${row.receiverName}」（订阅ID ${row.id}）后将停止推送，取消未完成任务，且无法恢复。确认删除？`,
        '删除订阅',
        { type: 'warning', confirmButtonText: '确认删除', cancelButtonText: '取消' }
      )
    } catch (error) {
      return
    }
    busy.value = true
    try {
      await deleteSubscription(props.meetingCode, row.id)
      editorOpen.value = false
      editing.value = null
      recordsFor.value = null
      connectivityFor.value = null
      historyFor.value = null
      guideOpen.value = false
      ElMessage.success('订阅已删除')
      await load()
    } finally {
      busy.value = false
    }
  }
  const labels = {
    PENDING: '待推送',
    SENDING: '推送中',
    RETRY: '等待重试',
    SUCCESS: '成功',
    FAILED: '失败',
    CANCELLED: '已取消',
  }

  async function load() {
    loading.value = true
    failed.value = false
    try {
      const { data } = await listSubscriptions(props.meetingCode)
      rows.value = data || []
    } catch (error) {
      failed.value = true
    } finally {
      loading.value = false
    }
  }
  function edit(row) {
    editing.value = row ? { ...row } : null
    editorOpen.value = true
  }
  async function toggle(row) {
    if (busy.value) return
    try {
      await ElMessageBox.confirm(
        row.enabled ? '停用后将取消尚未完成的推送，重新启用不会补发。' : '启用后接收新的报名结果。',
        row.enabled ? '停用订阅' : '启用订阅',
        { type: 'warning' }
      )
    } catch (error) {
      return
    }
    busy.value = true
    try {
      await setSubscriptionEnabled(props.meetingCode, row.id, !row.enabled)
      ElMessage.success('状态已更新')
      await load()
    } finally {
      busy.value = false
    }
  }
  async function test(row) {
    busy.value = true
    try {
      await testSubscription(props.meetingCode, row.id)
      ElMessage.success('测试已排队，可在推送记录中查看结果')
      recordsFor.value = row
    } finally {
      busy.value = false
    }
  }
  onMounted(load)
</script>

<template>
  <div>
    <div class="toolbar">
      <el-button type="primary" @click="edit(null)">新增订阅</el-button>
      <el-button :loading="loading" @click="load">刷新</el-button>
      <el-button @click="openGuide(null)">对接指引</el-button>
    </div>
    <el-alert v-if="failed" :closable="false" title="订阅加载失败，请重试" type="error" />
    <el-alert
      v-if="!loading && !failed && !rows.length"
      :closable="false"
      title="准备HTTPS回调地址 → 创建订阅并复制ID和密钥 → 测试连通性并启用；完整步骤见对接指引"
      type="info"
    />
    <el-table v-loading="loading" border :data="rows" empty-text="暂无报名结果订阅">
      <el-table-column label="订阅ID" min-width="120">
        <template #default="{ row }"><subscription-id :id="row.id" /></template>
      </el-table-column>
      <el-table-column label="接收方名称" min-width="140" prop="receiverName" />
      <el-table-column label="回调地址" min-width="220" prop="callbackUrl" show-overflow-tooltip />
      <el-table-column label="推送结果" min-width="150">
        <template #default="{ row }">
          {{
            row.statuses
              .split(',')
              .map((s) => ({ APPROVED: '审核通过', REJECTED: '审核拒绝', CANCELLED: '取消报名' }[s] || s))
              .join('、')
          }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="row.requiresSecretReset ? 'warning' : row.enabled ? 'success' : 'info'">
            {{ row.requiresSecretReset ? '需更新密钥' : row.enabled ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="最近推送" width="110">
        <template #default="{ row }">{{ labels[row.lastState] || '—' }}</template>
      </el-table-column>
      <el-table-column fixed="right" label="操作" width="560">
        <template #default="{ row }">
          <el-button :disabled="busy" type="text" @click="edit(row)">编辑</el-button>
          <el-button :disabled="busy" type="text" @click="openGuide(row)">接入配置</el-button>
          <el-button :disabled="busy || row.requiresSecretReset" type="text" @click="connectivityFor = row">
            测试连通性
          </el-button>
          <el-button :disabled="busy || !row.enabled || row.requiresSecretReset" type="text" @click="test(row)">
            模拟推送
          </el-button>
          <el-button :disabled="busy || !row.enabled || row.requiresSecretReset" type="text" @click="historyFor = row">
            历史补推
          </el-button>
          <el-button type="text" @click="recordsFor = row">记录</el-button>
          <el-button :disabled="busy || (row.requiresSecretReset && !row.enabled)" type="text" @click="toggle(row)">
            {{ row.enabled ? '停用' : '启用' }}
          </el-button>
          <el-button :disabled="busy" type="text" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <subscription-guide
      v-if="guideOpen"
      :meeting-code="meetingCode"
      :subscription-id="guideFor"
      :subscriptions="rows"
      @close="guideOpen = false"
      @create="createFromGuide"
    />
    <subscription-editor
      v-if="editorOpen"
      :meeting-code="meetingCode"
      :subscription="editing"
      @close="editorOpen = false"
      @saved="load"
    />
    <connectivity-test
      v-if="connectivityFor"
      :meeting-code="meetingCode"
      :subscription="connectivityFor"
      @close="connectivityFor = null"
    />
    <history-push v-if="historyFor" :meeting-code="meetingCode" :subscription="historyFor" @close="historyFor = null" />
    <delivery-records
      v-if="recordsFor"
      :meeting-code="meetingCode"
      :subscription="recordsFor"
      @close="recordsFor = null"
    />
  </div>
</template>

<style scoped>
  .toolbar {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }
</style>
